import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { PermissionService } from '~/app/services/authorization/permission.service';
import { RoleService } from '~/app/services/authorization/role.service';
import { groupPermissionsByCategory } from '../utils';
import { useRoleNameEditor } from './useRoleNameEditor';
import { useRolePermissionsEditor } from './useRolePermissionsEditor';

export function useRoleDetailPage() {
  const { roleId = '' } = useParams<{ roleId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const { t } = useTranslation('authorization');
  const queryClient = useQueryClient();

  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const organizationIdFromQuery = searchParams.get('organizationId');

  const organizationId = useMemo(() => {
    if (isSuperAdmin) {
      return organizationIdFromQuery ?? user?.organizationId ?? null;
    }
    return user?.organizationId ?? null;
  }, [isSuperAdmin, organizationIdFromQuery, user?.organizationId]);

  const useSystemRoleApi = isSuperAdmin && !organizationIdFromQuery;

  const permissionsQuery = useQuery({
    queryKey: ['permissions'],
    queryFn: () => PermissionService.listPermissions(),
    enabled: Boolean(roleId),
  });

  const roleQuery = useQuery({
    queryKey: [
      'role-detail',
      useSystemRoleApi ? 'system' : organizationId,
      roleId,
    ],
    queryFn: () =>
      useSystemRoleApi
        ? RoleService.findSystemRoleById(roleId)
        : RoleService.findRoleById(organizationId!, roleId),
    enabled: Boolean(roleId && (useSystemRoleApi || organizationId)),
  });

  const groupedPermissions = useMemo(
    () => groupPermissionsByCategory(permissionsQuery.data ?? []),
    [permissionsQuery.data],
  );

  const allPermissionKeys = useMemo(
    () => (permissionsQuery.data ?? []).map((permission) => permission.key),
    [permissionsQuery.data],
  );

  const refreshRole = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [
        'role-detail',
        useSystemRoleApi ? 'system' : organizationId,
        roleId,
      ],
    });

    if (organizationId) {
      void queryClient.invalidateQueries({
        queryKey: ['roles', organizationId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['roles-with-permissions', organizationId],
      });
    }
  }, [organizationId, queryClient, roleId, useSystemRoleApi]);

  const canAssignPermissions = hasPermission(
    PermissionKey.ROLES_ASSIGN_PERMISSIONS,
  );

  const editor = useRolePermissionsEditor({
    role: roleQuery.data ?? null,
    organizationId,
    useSystemRoleApi,
    canAssignPermissions,
    allPermissionKeys,
    onSaved: refreshRole,
  });

  const nameEditor = useRoleNameEditor({
    role: roleQuery.data ?? null,
    organizationId,
    useSystemRoleApi,
    onSaved: refreshRole,
  });

  const onBack = useCallback(() => {
    if (isSuperAdmin) {
      navigate('/admin/roles');
      return;
    }

    navigate(
      `/admin/roles${organizationIdFromQuery ? `?organizationId=${organizationIdFromQuery}` : ''}`,
    );
  }, [isSuperAdmin, navigate, organizationIdFromQuery]);

  const isLoading = permissionsQuery.isLoading || roleQuery.isLoading;
  const queryError = permissionsQuery.error ?? roleQuery.error;
  const error = queryError instanceof Error ? queryError : null;

  return {
    t,
    organizationId,
    useSystemRoleApi,
    role: roleQuery.data ?? null,
    groupedPermissions,
    isLoading,
    error,
    onBack,
    canAssignPermissions,
    userRoleName: user?.roleName ?? '',
    ...editor,
    ...nameEditor,
  };
}
